import {Component, Inject, Input, OnDestroy, OnInit} from '@angular/core';
import {AbstractControl} from '@angular/forms';
import {Subject} from 'rxjs';
import {startWith, takeUntil} from 'rxjs/operators';
import {FieldUiDynamicListObj, IdNameMxObj, KeyValueObj} from '../../../../../../../generated/generated-core.model';
import {MxDynamicFormService} from '../../../../services/mx-dynamic-form.service';
import {MxFormErrorMessageService} from '../../../../services/mx-form-error-message.service';
import {MxField} from '../../services/mxField';
import {MX_TRANSLATE_SERVICE_TOKEN, MxTranslateService} from '../../../../../../shared/services/mx-translate.service';

export class MxComboBoxConfig {
    autoSelect?: boolean;
    autoSelectIfDisabled?: boolean;
    listFilter?: any;
}

@Component({
    selector: 'mx-combo-box-field',
    standalone: false,
    templateUrl: './mx-combo-box-field.component.html',
    styleUrls: ['mx-combo-box-field.component.scss']
})
export class MxComboBoxFieldComponent extends MxField implements OnInit, OnDestroy {
    @Input() dependsOn: FieldUiDynamicListObj;
    @Input() disabledDependsOnField: string;

    filteredOptions: Array<KeyValueObj>;
    needSearch: boolean = false;

    private innerOptions: Array<KeyValueObj>;
    private comboBoxControl: AbstractControl;
    private destroy$$: Subject<null> = new Subject<null>();

    constructor(public errorService: MxFormErrorMessageService,
                private dynamicFormService: MxDynamicFormService,
                @Inject(MX_TRANSLATE_SERVICE_TOKEN) protected translateService: MxTranslateService) {
        super(errorService, translateService);
    }

    get options(): Array<KeyValueObj> {
        return this.innerOptions;
    }

    @Input()
    set options(value: Array<KeyValueObj | IdNameMxObj>) {
        this.innerOptions = Array.isArray(value)
            ? value.map(item => ('key' in item)
                ? item
                : {key: item.id, value: item.name, dependOn: undefined} as KeyValueObj)
            : (value as undefined);
        this.filteredOptions = this.innerOptions;
        this.needSearch = (this.innerOptions?.length ?? 0) > 6;
    }

    ngOnInit(): void {
        this.comboBoxControl = this.form.get([this.name]);
        this.filteredOptions = this.options;
        this.needSearch = this.options.length > 6;
        if (this.disabledDependsOnField) {
            let dependOnControl: AbstractControl = this.form.get([this.disabledDependsOnField]);
            dependOnControl.valueChanges
                .pipe(
                    takeUntil(this.destroy$$),
                    startWith(dependOnControl.value),
                )
                .subscribe(dependOnValue => {
                    if (dependOnValue) {
                        this.filteredOptions = this.options.filter(opt => opt.dependOn === dependOnValue);
                    }
                });
        }
        let initValue = this.comboBoxControl.value;
        if (this.dependsOn && this.dependsOn.dependField) {
            let dependOnControl: AbstractControl = this.form.get([this.dependsOn.dependField]);
            dependOnControl.valueChanges
                .pipe(
                    takeUntil(this.destroy$$),
                    startWith(dependOnControl.value),
                )
                .subscribe(dependOnValue => {
                    if (dependOnValue && (typeof dependOnValue !== 'object' || (dependOnValue.key && dependOnValue.value))) {
                        let autoSelect = this.fieldConfig && this.fieldConfig.comboBoxConfig && this.fieldConfig.comboBoxConfig.autoSelect;
                        let autoSelectIfDisabled = this.fieldConfig && this.fieldConfig.comboBoxConfig
                            && this.fieldConfig.comboBoxConfig.autoSelectIfDisabled;
                        if (autoSelect === false) {
                            this.comboBoxControl.reset();
                        }
                        let valueData = dependOnValue;
                        if (dependOnValue.key && dependOnValue.value) {
                            valueData = dependOnValue.key;
                        }
                        let property: { [index: string]: string } = {
                            [this.dependsOn.dependField]: valueData
                        };
                        if (this.fieldConfig && this.fieldConfig.comboBoxConfig && this.fieldConfig.comboBoxConfig.listFilter) {
                            Object.assign(property, this.fieldConfig.comboBoxConfig.listFilter);
                        }
                        this.dynamicFormService.getUiDynamicCombo({
                            dependField: this.dependsOn.dependField,
                            property: property,
                            optionsList: this.dependsOn.optionsList
                        }).subscribe(response => {
                            this.options = response;
                            this.filteredOptions = response;
                            if (this.options.length > 0 && (this.formReadonlyMode
                                || (this.comboBoxControl.disabled && autoSelectIfDisabled !== false)
                                || (this.comboBoxControl.enabled && autoSelect !== false))) {
                                if (initValue && this.options.map(value => value.key).indexOf(initValue) > -1) {
                                    this.comboBoxControl.setValue(initValue);
                                } else if (!initValue) {
                                    this.comboBoxControl.setValue(this.options[0].key);
                                }
                            }
                        });
                    } else {
                        this.comboBoxControl.reset();
                        this.options = [];
                        this.filteredOptions = [];
                    }
                });
        }
    }

    ngOnDestroy(): void {
        this.destroy$$.next(null);
    }

    public getOptionValueByKey(key: any): string {
        let option: KeyValueObj;
        if (Array.isArray(this.options)) {
            option = this.options.find( opt => opt.key === key);
        }
        return option ? option.value : '';
    }

}
